import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import BrandState from '../../components/BrandState'
import { type SxProps, type Theme } from '@mui/material/styles'
import { type CSSProperties, useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import {
  type SalePTBFFixation,
  useCancelSalePTBFFixationByContractMutation,
  useGetSalePTBFFixationsQuery,
} from '../../store/api/salePTBFFixationsApi'
import SalePTBFFixationFormDialog from './SalePTBFFixationFormDialog'
import SalePTBFFixationPrintDialog from './SalePTBFFixationPrintDialog'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import ResizeHandle from '../../components/ResizeHandle'
import ConfirmDialog from '@/components/ConfirmDialog'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof SalePTBFFixation
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

const ALL_ROWS_PAGE_SIZE = 1_000_000

/**
 * Cột freeze khi scroll ngang — skill `/table-style` §7.6. "Is Uploaded?"
 * giữ ở vị trí 0 (không freeze) → khi scroll phải sẽ khuất sau Contract No.
 * Width fallback theo viewport: mobile hẹp hơn để chừa chỗ scroll.
 */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['contractSale'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = {
  contractSale: 130,
}
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = {
  contractSale: 110,
}

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const FIX_REPEATING_COLUMNS: ColumnDef[] = Array.from({ length: 30 }, (_, i) => i + 1).flatMap((n) => [
  { key: `fix${n}Date` as keyof SalePTBFFixation, label: `Date Fix ${n}`, kind: 'date' as ColumnKind },
  { key: `fix${n}LotsFixed` as keyof SalePTBFFixation, label: `Lots Fixed ${n}`, kind: 'number' as ColumnKind, align: 'right' as const },
  { key: `fix${n}LevelFixed` as keyof SalePTBFFixation, label: `Level Fixed ${n}`, kind: 'number' as ColumnKind, align: 'right' as const },
])

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'uploadText', label: 'Is Uploaded?' },
  { key: 'contractSale', label: 'Contract No' },
  { key: 'releaseDate', label: 'IssuedDate', kind: 'date' },
  { key: 'buyersRef', label: "Buyer's Ref" },
  { key: 'cpCode', label: 'Client Ref' },
  { key: 'name', label: 'Client Name' },
  { key: 'quality', label: 'Grade' },
  { key: 'description', label: 'Description' },
  { key: 'mtons', label: 'Mtons', kind: 'number', align: 'right' },
  { key: 'noOfLots', label: 'No. of Lots', kind: 'number', align: 'right' },
  { key: 'ptbfDiffs', label: 'PTBF Diffs', kind: 'number', align: 'right' },
  { key: 'futuresMonth', label: 'Futures Month' },
  { key: 'balanceLotsTbf', label: 'Balance Lots TBF', kind: 'number', align: 'right' },
  { key: 'finalPrice', label: 'Final Price', kind: 'number', align: 'right' },
  { key: 'customerSignedText', label: 'Customer Signed?' },
  ...FIX_REPEATING_COLUMNS,
]

const SUM_KEYS = new Set<keyof SalePTBFFixation>([
  'mtons',
  'noOfLots',
  'balanceLotsTbf',
  ...Array.from({ length: 30 }, (_, i) => `fix${i + 1}LotsFixed` as keyof SalePTBFFixation),
])
const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SalePTBFFixation[], key: keyof SalePTBFFixation): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

export default function SalePTBFFixationListView() {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  // Row selection toggle: click row → focus, click lại → bỏ focus.
  const {
    selectedId: selectedContractNo,
    setSelectedId: setSelectedContractNo,
    toggleSelect: toggleSelectContract,
  } = useRowSelection<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SalePTBFFixation> | null>(null)
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SalePTBFFixation
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [printLang, setPrintLang] = useState<'en' | 'vn' | null>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [cancelFixation, { isLoading: isDeleting }] = useCancelSalePTBFFixationByContractMutation()
  const { getWidth, startResize } = useColumnWidths('sale-contract.ptbf-fixation')

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_contract',
    prefKey: 'columns',
    subKey: 'ptbf-fixation',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-contract.ptbf-fixation',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  // Freeze styling handled bởi CSS `.app-sticky-col` — chỉ cần add className.

  const { data, isLoading, isFetching, error } = useGetSalePTBFFixationsQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
    },
    // Mỗi lần đổi tab Sales Contract → component remount → refetch lại data.
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const selectedRow = useMemo(
    () => filteredRows.find((row) => (row.contractSale ?? row.contractNo) === selectedContractNo) ?? null,
    [filteredRows, selectedContractNo],
  )
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number' && SUM_KEYS.has(col.key))
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const minTableWidth = Math.max(1200, visibleColumns.length * 88)

  const toggleSort = (key: keyof SalePTBFFixation) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  // Concept (tham khảo /purchase/contract/plans): gộp New + Edit thành 1 nút.
  // Form đã unified — chỉ truyền contractNo (nullable) làm initial cho
  // Autocomplete bên trong. Mode auto-detect bên trong form theo subform rows.
  // CRUD role giữ nguyên (canEdit + create/update endpoints không đổi).
  const handleEditOrNew = () => {
    setFormOpen(true)
  }

  const handleDelete = () => {
    setConfirmDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedRow) return
    const contractNo = selectedRow.contractSale ?? selectedRow.contractNo
    if (!contractNo) return
    setConfirmDeleteOpen(false)
    try {
      await cancelFixation(contractNo).unwrap()
      setSelectedContractNo(null)
      snack.success('Successfully Deleted')
    } catch (err) {
      snack.error(getErrorMessage(err, 'Could not delete PTBF Fixation.'))
    }
  }

  const cellValue = (row: SalePTBFFixation, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  const idRangeText = useMemo(() => {
    const ids = rows.map((r) => Number(r.id)).filter(Number.isFinite)
    if (!ids.length) return 'ID: —'
    return `ID: ${Math.min(...ids)}-${Math.max(...ids)}`
  }, [rows])

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              color={selectedRow ? 'primary' : 'success'}
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={handleEditOrNew}
              startIcon={selectedRow ? <EditOutlinedIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
              title={selectedRow ? 'Edit' : 'New'}
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{selectedRow ? 'Edit' : 'New'}</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow}
              onClick={() => setPrintLang('en')}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Pr - Fixation E"
            >
              {/* "E"/"V" luôn show để phân biệt 2 Print buttons. */}
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Pr - Fixation{' '}</Box>
              E
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow}
              onClick={() => setPrintLang('vn')}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Pr - Fixation V"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Pr - Fixation{' '}</Box>
              V
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || isDeleting || !canEdit}
              onClick={handleDelete}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              title="Delete"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
          <Box
            className="app-dataframe-toolbar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              flexWrap: 'wrap',
              minHeight: 30,
              py: 0.125,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
              {(isLoading || isFetching) && <CircularProgress size={15} />}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
              >
                Total: {filteredRows.length}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
              >
                Loaded: {baseRows.length}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
              >
                {idRangeText}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={rowsPerPage === -1 ? 0 : page}
                onPageChange={(_, value) => setPage(value)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
            {error ? (
              <BrandState
                tone="error"
                title="Could not load PTBF Fixations"
                description="Please refresh the page or contact the system administrator if the issue continues."
              />
            ) : isLoading && baseRows.length === 0 ? (
              <BrandState tone="loading" title="Loading…" description="Please wait." />
            ) : rows.length === 0 ? (
              <BrandState
                title="No PTBF Fixations found"
                description="Try adjusting the table filters above."
              />
            ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'none',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: SalePTBFFixation) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={row != null && (row.contractSale ?? row.contractNo) === selectedContractNo}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (!row) return
                        toggleSelectContract(row.contractSale ?? row.contractNo)
                      }}
                    />
                  )
                },
              }}
            />
            )}
          </Box>
      </Box>

      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={filterPopover.close}
        maxWidth={360}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPage(0)
            }}
            placeholder="Contract no, client, quality..."
            label="Search fixations"
            onClear={filterPopover.close}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(String(colPopover.key), v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      <SalePTBFFixationFormDialog
        open={formOpen}
        contractNo={selectedRow?.contractNo}
        onClose={() => setFormOpen(false)}
      />
      <SalePTBFFixationPrintDialog
        open={printLang != null}
        language={printLang ?? 'en'}
        fixation={selectedRow}
        onClose={() => setPrintLang(null)}
        canEdit={canEdit}
      />
      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete PTBF Fixation"
        message={`Do you want to delete this PTBF Fixation = '${selectedRow?.contractSale ?? selectedRow?.contractNo ?? ''}'?`}
        confirmText="Delete"
        destructive
        loading={isDeleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
